// Carousel.js
import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";

// Konstante
const SLIDE_WIDTH_PX = 500;
const GAP_WIDTH_PX = 20;
const BASE_ANIMATION_DURATION_S = 40;
const BASE_ANIMATION_WIDTH_PX = 5000;

const scrollAnimation = (totalWidth) => keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-${totalWidth}px));
  }
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  margin: 3rem 0;
  position: relative;

  &:hover > div {
    animation-play-state: paused;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 100px;
    height: 100%;
    z-index: 1;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--surface, #f8f9fa), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--surface, #eff6ff), transparent);
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  gap: ${GAP_WIDTH_PX}px;
  animation: ${(props) => scrollAnimation(props.totalWidth)}
    ${(props) => props.duration}s linear infinite;
  will-change: transform;
`;

const Slide = styled.div`
  flex: 0 0 ${(props) => props.$flex || "30%"};
  height: ${(props) => props.$height || "250px"};
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  background: rgba(18, 92, 251, 0.1);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.12);
  }

  &:hover > div {
    opacity: 1;
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.$fit || "cover"};
  display: block;
  border-radius: 20px;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
  padding: 1rem;
  pointer-events: none;
`;

const Carousel = ({ images, objfit, flex, height }) => {
  const [carouselProps, setCarouselProps] = useState({
    totalWidth: 0,
    duration: 0,
  });
  const trackRef = useRef(null);

  useEffect(() => {
    if (!images || images.length === 0) return;

    const singleSetWidth = (SLIDE_WIDTH_PX + GAP_WIDTH_PX) * images.length;
    const calculatedDuration =
      (singleSetWidth / BASE_ANIMATION_WIDTH_PX) * BASE_ANIMATION_DURATION_S;

    setCarouselProps({
      totalWidth: singleSetWidth,
      duration:
        calculatedDuration > 0 ? calculatedDuration : BASE_ANIMATION_DURATION_S,
    });
  }, [images]);

  const generateYear = (index) => {
    return 2024;
  };

  const duplicatedImages = [...images, ...images, ...images];

  return (
    <CarouselWrapper>
      <CarouselTrack
        ref={trackRef}
        totalWidth={carouselProps.totalWidth}
        duration={carouselProps.duration}>
        {duplicatedImages.map((src, index) => (
          <Slide key={index} $flex={flex} $height={height}>
            <SlideImage
              src={src}
              alt={`Slika ${index + 1}`}
              style={{ objectFit: objfit }}
            />
            <Overlay>UNINP Hackathon {generateYear(index)}</Overlay>
          </Slide>
        ))}
      </CarouselTrack>
    </CarouselWrapper>
  );
};

export default Carousel;
